import {
  Button,
  Divider,
  Grid2 as Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { useGlobalContext } from "context/GlobalContext";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { docCodeGeneratorGlobal } from "utils/functions/docCodeGenerator";
import { useProductContext } from "../context";
import { fetchProductPmcChecklistApi } from "API/apis/product/getPmcChecklist";
import { message } from "constants/responses/message";
import { approveChecklistApi } from "API/apis/product/approveChecklist";

const PaperWrapper = styled(Paper)(({ theme }) => ({
  width: {
    xs: "100%",
    sm: "auto",
  },
  padding: theme.spacing(2),
  "&.isError": {
    backgroundColor: "#ffe5e5 !important",
  },
}));

export const PmcHandoverChecklistPreview = () => {
  const { id } = useParams();
  const { setIsInitializing, setError, setSuccess, pageAccess } =
    useGlobalContext();
  const { setReloadRequired, reloadRequired } = useProductContext();
  const [data, setData] = useState({});
  const [isApproved, setIsApproved] = useState();

  useEffect(() => {
    const fetchData = async (lotPartId) => {
      try {
        setIsInitializing(true);
        const { data, status } = await fetchProductPmcChecklistApi(lotPartId);

        if (status !== 200) {
          throw new Error(message.error.generalFailure);
        }
        setData(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData(id);
  }, [id, reloadRequired]);
  const handleChangeApprovalStatus = async () => {
    try {
      const { data, status } = await approveChecklistApi({
        productLotProcessingId: id,
        status: !isApproved,
      });
      if (status === 204) {
        throw new Error(message.info.noRecordsModified);
      }
      if (status !== 200) {
        throw new Error(data.message);
      }
      setIsApproved(!isApproved);
      setSuccess(data.message);
      setReloadRequired(!reloadRequired);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <>
      <Box justifyContent="space-between" alignItems="center">
        <Typography variant="h5" sx={{ mb: 2 }}>
          PMC Handover Checklist -
          {docCodeGeneratorGlobal({
            id: data.productLotProcessingId,
            docType: "HANDOVER_CHECKLIST",
          })}
        </Typography>
        <FlexBox alignItems="center">
          {pageAccess.canApproval && (
            <Button
              variant="contained"
              onClick={handleChangeApprovalStatus}
              color={!isApproved ? "success" : "error"}
            >
              {!isApproved ? "Approve" : "Un-Approve"}
            </Button>
          )}
          <Divider variant="middle" />
          <Typography component="h5">
            {isApproved
              ? "Approved By : "
              : data.approvedOn
              ? "UnApproved By : "
              : ""}
            {data.approvedBy || ""}
          </Typography>
        </FlexBox>
      </Box>

      <Grid container spacing={2} mt={4}>
        <Grid container xs={12} md={4}>
          <PaperWrapper className={data?.projectEndScope || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Project End Scope
            </Typography>
            <Divider />
            <Typography variant="body2">{data.projectEndScope}</Typography>
          </PaperWrapper>

          <PaperWrapper
            className={data?.midProjectMilestones?.length > 0 || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Mid Project Milestones
            </Typography>
            <Divider />
            <List
              sx={{
                paddingLeft: 1,
                listStyleType: "disc",
                listStylePosition: "inside",
              }}
            >
              {data.midProjectMilestones?.map((item, index) => (
                <ListItem
                  sx={{ display: "list-item", paddingY: 0.5, paddingX: 0 }}
                  key={index}
                >
                  {item}
                </ListItem>
              ))}
            </List>
          </PaperWrapper>

          <PaperWrapper
            className={data?.thingsInClientScope?.length > 0 || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Things In Client-scope
            </Typography>
            <Divider />
            <List
              sx={{
                paddingLeft: 1,
                listStyleType: "disc",
                listStylePosition: "inside",
              }}
            >
              {data.thingsInClientScope?.map((item, index) => (
                <ListItem
                  sx={{ display: "list-item", paddingY: 0.5, paddingX: 0 }}
                  key={index}
                >
                  {item}
                </ListItem>
              ))}
            </List>
          </PaperWrapper>

          <PaperWrapper className={data?.thingsInClientScopeNotes || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Things In Client-scope-notes
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.thingsInClientScopeNotes}
            </Typography>
          </PaperWrapper>

          <PaperWrapper
            className={
              data?.sampleFinishDevelopmentRequired?.length > 0 || "isError"
            }
          >
            <Typography variant="body2" fontWeight="bold">
              Sample / Finish development required
            </Typography>
            <Divider />
            <List
              sx={{
                paddingLeft: 1,
                listStyleType: "disc",
                listStylePosition: "inside",
              }}
            >
              {data.sampleFinishDevelopmentRequired?.map((item, index) => (
                <ListItem
                  sx={{ display: "list-item", paddingY: 0.5, paddingX: 0 }}
                  key={index}
                >
                  {item}
                </ListItem>
              ))}
            </List>
          </PaperWrapper>

          <PaperWrapper
            className={data?.sampleFinishDevelopmentNotes || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Sample Finish Development notes
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.sampleFinishDevelopmentNotes}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.specificPurchaseRequired || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Specific Purchase Required
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.specificPurchaseRequired}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.specificPurchaseNotes || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Specific Purchase Notes
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.specificPurchaseNotes}
            </Typography>
          </PaperWrapper>

          <PaperWrapper
            className={data?.specificRequirementMetal?.length > 0 || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Specific Requirement Metal
            </Typography>
            <Divider />
            <List
              sx={{
                paddingLeft: 1,
                listStyleType: "disc",
                listStylePosition: "inside",
              }}
            >
              {data.specificRequirementMetal?.map((item, index) => (
                <ListItem
                  sx={{ display: "list-item", paddingY: 0.5, paddingX: 0 }}
                  key={index}
                >
                  {item}
                </ListItem>
              ))}
            </List>
          </PaperWrapper>

          <PaperWrapper
            className={data?.specificRequirementTeakWood || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Specific Requirement Teak-wood
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.specificRequirementTeakWood}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.siteMeasurementRequired || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Site Measurement Required
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.siteMeasurementRequired}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.siteMeasurementNotes || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Site Measurement Notes
            </Typography>
            <Divider />
            <Typography variant="body2">{data.siteMeasurementNotes}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.clientSendingProduct || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Is Client sending any products to us?
            </Typography>
            <Divider />
            <Typography variant="body2">{data.clientSendingProduct}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.dispatchPlanClarity || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Dispatch Plan Clarity
            </Typography>
            <Divider />
            <Typography variant="body2">{data.dispatchPlanClarity}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.clientsBillingAddress || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Client&apos;s Billing Address
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.clientsBillingAddress}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.callingDispatchContainer || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Who is calling the dispatch container
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.callingDispatchContainer}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.dispatchTerms || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Dispatch Terms
            </Typography>
            <Divider />
            <Typography variant="body2">{data.dispatchTerms}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.earlyDelivery || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Early Delivery
            </Typography>
            <Divider />
            <Typography variant="body2">{data.earlyDelivery}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.approvedQuotation || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Approved-Quotation
            </Typography>
            <Divider />
            <Typography variant="body2">{data.approvedQuotation}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.po || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Po
            </Typography>
            <Divider />
            <Typography variant="body2">{data.po}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.advanceReceived || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Advance Received
            </Typography>
            <Divider />
            <Typography variant="body2">{data.advanceReceived}</Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.advanceConfirmation || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Advance Confirmation
            </Typography>
            <Divider />
            <Typography variant="body2">{data.advanceConfirmation}</Typography>
          </PaperWrapper>

          <PaperWrapper
            className={data?.designDrawingsDesignScopeFromClient || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Design Drawings Design Scope From Client
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.designDrawingsDesignScopeFromClient}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.productToPcdCodeMapping || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Product To Pcd Code Mapping
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.productToPcdCodeMapping}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.dispatchDateConfirmation || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Dispatch Date Confirmation
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.dispatchDateConfirmation}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.shortTimelineApprovals || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Short Timeline Approvals
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.shortTimelineApprovals}
            </Typography>
          </PaperWrapper>

          <PaperWrapper
            className={data?.pythaHanmacShopDrawingApprovalFromSk || "isError"}
          >
            <Typography variant="body2" fontWeight="bold">
              Pytha/HANMAC Shop Drawing Approval From Sk
            </Typography>
            <Divider />
            <Typography variant="body2">
              {data.pythaHanmacShopDrawingApprovalFromSk}
            </Typography>
          </PaperWrapper>

          <PaperWrapper className={data?.anyOtherProjectNotes || "isError"}>
            <Typography variant="body2" fontWeight="bold">
              Any Other Project Notes
            </Typography>
            <Divider />
            <Typography variant="body2">{data.anyOtherProjectNotes}</Typography>
          </PaperWrapper>
        </Grid>
      </Grid>
    </>
  );
};
